import { useState, useCallback, useEffect } from 'react';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import type { StudioTeamInput, StudioTeamResult } from './useStudioTeam';

/** Mirrors server/services/studioTeamV2Executor.ts's StudioTeamV2Event 'step' shape. */
export interface StudioTeamV2StepEvent {
  type: 'step';
  stepIndex: number;
  agent: string;
  ok: boolean;
  action: string | null;
  next: string | null;
  retries: number;
  errors: string[];
  warnings: string[];
  usage: { inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheCreateTokens: number };
}

interface V2Draft {
  title: string | null;
  artist: string | null;
  lyricsWithTags: string;
  style: string;
  excludedStyle: string;
  summary: string;
  artPrompt: string | null;
}

interface V2DoneEvent {
  delivered?: boolean;
  draft?: V2Draft;
  usage?: StudioTeamV2StepEvent['usage'];
}

/**
 * Turns v2's already-structured draft into the same sentinel-delimited text
 * shape src/shared/studioTeamBlockParser.ts's extractBlocks() already knows
 * how to parse -- so the rest of StudioTeamPanel.tsx (block cards, Save to
 * Vault, Send to Art, Send to Suno) needs zero v2-specific logic. v2 has no
 * prose walkthrough to include (each agent's reasoning is discarded by
 * design, see song_state.py's I4 principle) -- only the 6 final blocks.
 */
export function synthesizeText(draft: V2Draft): string {
  // parseTitleArtist's regex uses \s* around the colon, which (like most
  // regex flavors) matches newlines too -- an empty "Title: " line lets it
  // walk straight into the next line and capture "Artist:" as the title.
  // Only emit a Key: line when there's real content, so an empty field
  // just produces an empty block body (which parseTitleArtist already
  // handles correctly: no match, fall back to the caller's default) instead
  // of a line for the regex to walk across.
  const titleArtistLines: string[] = [];
  if (draft.title) titleArtistLines.push(`Title: ${draft.title}`);
  if (draft.artist) titleArtistLines.push(`Artist: ${draft.artist}`);

  const parts = [
    `<<<SUNO_BLOCK_1_LYRICS>>>\n${draft.lyricsWithTags || ''}\n<<<END_BLOCK_1>>>`,
    `<<<SUNO_BLOCK_2_STYLE>>>\n${draft.style || ''}\n<<<END_BLOCK_2>>>`,
    `<<<SUNO_BLOCK_3_EXCLUDED_STYLE>>>\n${draft.excludedStyle || ''}\n<<<END_BLOCK_3>>>`,
    `<<<SUNO_BLOCK_4_TITLE_ARTIST>>>\n${titleArtistLines.join('\n')}\n<<<END_BLOCK_4>>>`,
    `<<<SUNO_BLOCK_5_SUMMARY>>>\n${draft.summary || ''}\n<<<END_BLOCK_5>>>`,
  ];
  if (draft.artPrompt) {
    parts.push(`<<<SUNO_BLOCK_6_IMAGE_PROMPT>>>\n${draft.artPrompt}\n<<<END_BLOCK_6>>>`);
  }
  return parts.join('\n\n');
}

/** v1's form gathers Title/Genre/Mood/Setting/Vocal/Subject/Constraints as
 * separate fields; v2's /generate endpoint takes one free-text brief. Same
 * form, same fields -- just composed differently on submit. */
export function buildV2Brief(input: StudioTeamInput): string {
  const lines = [`Title: ${input.title}`, `Genre: ${input.genre}`];
  if (input.mood) lines.push(`Mood: ${input.mood}`);
  if (input.setting) lines.push(`Setting: ${input.setting}`);
  if (input.vocal) lines.push(`Vocal: ${input.vocal}`);
  if (input.subject) lines.push(`Subject: ${input.subject}`);
  if (input.constraints) lines.push(`Constraints: ${input.constraints}`);
  return lines.join('\n');
}

// Module-level singleton, same pattern as useStudioTeam.ts, so the result
// survives a tab switch.
let _result: StudioTeamResult | null = null;
let _brief: StudioTeamInput | null = null;
const _listeners = new Set<() => void>();
function notify() { _listeners.forEach((fn) => fn()); }
function setCached(result: StudioTeamResult | null, brief: StudioTeamInput | null) {
  _result = result;
  _brief = brief;
  notify();
}

export function useStudioTeamV2() {
  const [, force] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<StudioTeamV2StepEvent[]>([]);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);

  const generate = useCallback(async (input: StudioTeamInput): Promise<StudioTeamResult | null> => {
    setLoading(true);
    setError(null);
    setSteps([]);
    try {
      const res = await fetchWithCsrf('/api/studio-team-v2/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: input.title,
          artist: 'Unknown Artist',
          brief: buildV2Brief(input),
          // This toggle's v1 form has no instrumental/DEX controls yet --
          // always vocal mode, no DEX critique pass, from this entry point.
          mode: 'vocal',
          dex: false,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || `Studio Team v2 failed (${res.status})`);
      }
      if (!res.body) throw new Error('Response had no body');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let doneEvent: V2DoneEvent | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line) as { type: string } & Record<string, unknown>;
          if (event.type === 'step') {
            setSteps((prev) => [...prev, event as unknown as StudioTeamV2StepEvent]);
          } else if (event.type === 'error') {
            throw new Error((event.message as string) || 'Generation failed');
          } else if (event.type === 'done') {
            doneEvent = event as unknown as V2DoneEvent;
          }
          // 'heartbeat' — nothing to do
        }
      }

      if (!doneEvent?.delivered || !doneEvent.draft) {
        throw new Error('Run ended without delivering a song — check the step log above for where it stopped.');
      }
      const draft = doneEvent.draft;
      const usage = doneEvent.usage;
      const result: StudioTeamResult = {
        provider: 'claude',
        model: 'claude-sonnet-4-5',
        text: synthesizeText(draft),
        usage: usage ? { inputTokens: usage.inputTokens, outputTokens: usage.outputTokens } : undefined,
      };
      setCached(result, input);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => { setCached(null, null); setError(null); setSteps([]); }, []);

  return { result: _result, brief: _brief, loading, error, steps, generate, reset };
}
